import { NavMenuItem } from 'melp-design/components';
import { useCallback } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useLinks } from 'config/links';
import { PermissionOptions } from '../../../constants/Administrators';
import { useMe } from '../../../state/Administrators';
import { Permission } from '../../../types/Administrators';
import { LinkItem } from '../../../types/Common';

export default function MenuMap() {
  const location = useLocation();
  const {
    me,
    getPermissionsByModule,
    isRoot,
    isMelpAdmin,
    isFeatureEnabled,
    hasViewPermission,
  } = useMe();
  const links = useLinks();

  const checkIfPermissionsAllowed = useCallback(
    (permissions: Permission[], matchAll?: boolean) => {
      if (matchAll) {
        const noPermissions = permissions.some(
          (permission: Permission) =>
            permission.permission === PermissionOptions.None,
        );
        return !noPermissions;
      }
      return permissions.some(
        (permission: Permission) =>
          permission.permission === 'view' || permission.permission === 'edit',
      );
    },
    [],
  );

  if (!me) return null;
  if (
    !isFeatureEnabled('advancedPermissions') &&
    !isRoot &&
    !me.permissions?.length &&
    !isMelpAdmin
  ) {
    return null;
  }

  const hasAccessToLink = (link: LinkItem): boolean => {
    const hasCorrectRole = !!me?.role && link.roles.includes(me.role);

    if (!hasCorrectRole) {
      return false;
    }

    if (isFeatureEnabled('advancedPermissions')) {
      const childrenLinks = 'links' in link ? link.links : [];

      // A group link is accessible when at least one of its children is.
      if (childrenLinks.length) {
        return childrenLinks.some(hasAccessToLink);
      }

      return hasViewPermission(link.permission);
    }

    if (isRoot) {
      return true;
    }

    if (!link.module) {
      return true;
    }

    const permissions = getPermissionsByModule(link.module);
    const permissionsAllowed = checkIfPermissionsAllowed(
      permissions,
      link.matchAllModules,
    );

    return permissionsAllowed;
  };

  const isPathActive = (path?: string) => {
    if (!path) return false;

    return !!matchPath(location.pathname, {
      path,
      exact: false,
      strict: false,
    });
  };

  return (
    <>
      {links.filter(hasAccessToLink).map((link) => {
        const childrenLinks = 'links' in link ? link.links : [];

        return (
          <NavMenuItem
            key={link.title}
            id={link.title}
            label={link.title}
            path={link.to}
            active={
              isPathActive(link.to) ||
              childrenLinks.some((childLink) => isPathActive(childLink.to))
            }
            badge={link.badge}
            childrenRoutes={childrenLinks
              .filter(hasAccessToLink)
              .map((childLink) => ({
                id: childLink.title,
                label: childLink.title,
                path: childLink.to,
                badge: childLink.badge,
              }))}
          />
        );
      })}
    </>
  );
}
